import React from 'react';
import { Neo, Views, NeoGrid } from '@singularsystems/neo-react';
import { observer } from 'mobx-react';
import TransctionsVM from './TransactionsVM';
import CollapsibleCard from '../../../App/Components/CollapsibleCard';
import { TextConstants } from '../../../Common/TextConstants';
import TransactionsLookup from '../Models/Lookups/TransactionLookup';
import { Validation } from '@singularsystems/neo-core';
import FileDownloadComponent from '../../../Common/Components/FileDownload/FileDownloadComponent';
import Loading from '../../../App/Components/Loading/Loading';
import InfoLabelHover from '../../../Common/Components/InfoLabelHover/InfoLabelHover';

@observer
export default class TransactionsView extends Views.ViewBase<TransctionsVM> {
    constructor(props: unknown) {
        super("Transaction", TransctionsVM, props);
    }

    public render() {
        return (
            <div>
                {(this.viewModel.taskRunner.isBusy) && <Loading />}
                <CollapsibleCard title={TextConstants.Titles.Transactions} isExpanded={true}>
                    <Neo.Form
                        model={this.viewModel.transactionsSearchCriteria}
                        onSubmit={() => this.taskRunner.waitFor(this.viewModel.search())}
                        validationDisplayMode={Validation.DisplayMode.AfterBlur}>
                        <Neo.GridLayout lg={4}>
                            <Neo.FormGroup label={TextConstants.Labels.Broker}
                                bindContext={this.viewModel.transactionsSearchCriteria.meta.serviceProviderGroupingName}>
                                <Neo.AutoCompleteDropDown
                                    itemSource={this.viewModel.serviceProviderApiClient.getServiceProviderFileGroupingListByCriteria}
                                    bind={this.viewModel.transactionsSearchCriteria.meta.staticServiceProviderFileGroupingID}
                                    bindDisplay={this.viewModel.transactionsSearchCriteria.meta.serviceProviderGroupingName}
                                    displayMember={"serviceProviderGroupingName"} 
                                    items={this.viewModel.serviceProviderApiClient.getServiceProviderFileGroupingListByCriteria(" ").then((arr) => arr.data
                                    .sort((x,y) => x.serviceProviderGroupingName.localeCompare(y.serviceProviderGroupingName)))}    
                                />
                            </Neo.FormGroup>
                            <Neo.FormGroup bindContext={this.viewModel.transactionsSearchCriteria.meta.clientAccountNumber}
                                label={TextConstants.Labels.ClientNumber}>
                                <Neo.AutoCompleteDropDown
                                    minCharacters={3}
                                    delayMs={1500}                                
                                    bind={this.viewModel.transactionsSearchCriteria.meta.clientAccountNumber}
                                    bindDisplay={this.viewModel.transactionsSearchCriteria.meta.clientAccountNumber}
                                    displayMember={"clientDescription"} 
                                    items={this.viewModel.filteredClientAccounts}
                                    itemSource={async (searchText) => this.viewModel.clientAccounts.filter(clientAccount => clientAccount.clientDescription.toLowerCase().includes(searchText.toLowerCase()))}       
                                />
                            </Neo.FormGroup>
                            <Neo.FormGroup
                                label={TextConstants.Labels.Account}
                                bind={this.viewModel.transactionsSearchCriteria.meta.rawAccountNumber}
                            />
                            <Neo.FormGroup bindContext={this.viewModel.transactionsSearchCriteria.meta.instrument} >
                                <Neo.AutoCompleteDropDown
                                    minCharacters={3}
                                    delayMs={1500}                                
                                    label={TextConstants.Labels.Instrument}
                                    filterOption={x => x.data.description != null}
                                    bind={this.viewModel.transactionsSearchCriteria.meta.instrumentID}
                                    bindDisplay={this.viewModel.transactionsSearchCriteria.meta.instrument}
                                    displayMember={"description"} 
                                    pageSize={10}
                                    items={this.viewModel.filteredInstruments}
                                    itemSource={async (searchText) => this.viewModel.instruments.filter(instrument => instrument.description?.toLowerCase().includes(searchText.toLowerCase()))}
                                />
                            </Neo.FormGroup>


                        </Neo.GridLayout>
                        <div className="row">
                            <div className="col-md-3">
                                <Neo.FormGroup
                                    appendText="fa-calendar"
                                    label={TextConstants.Labels.StartDate}
                                    bind={this.viewModel.transactionsSearchCriteria.meta.rangeStartDate} />
                            </div>
                            <div className="col-md-3">
                                <Neo.FormGroup
                                    appendText="fa-calendar"
                                    label={TextConstants.Labels.EndDate}
                                    bind={this.viewModel.transactionsSearchCriteria.meta.rangeEndDate} />
                            </div>
                            <div className="col float-right">
                                <Neo.Button
                                    isSubmit
                                    variant="primary" className="form-btn float-right col-md-3"
                                >{TextConstants.Buttons.Search}</Neo.Button>
                            </div>
                        </div>
                    </Neo.Form>
                    {this.viewModel.transactionsPageManager.totalRecords === 0 &&
                        <div className="col-md-12 p=0 text-center">
                            {TextConstants.GeneralText.NoRecords}
                        </div>
                    }

                    {this.viewModel.transactionsPageManager.totalRecords > 0 &&
                        <>
                            {this.viewModel.transactionsPageManager.totalRecords > 0 &&
                                <div className="pb-5">
                                    <FileDownloadComponent
                                        viewModel={this.viewModel.fileDownloadVM}
                                        onDownloadCSV={() => this.viewModel.downloadTransactionsFile()}
                                    />
                                </div>
                            }
                            <Neo.Pager pageManager={this.viewModel.transactionsPageManager} pageControlProps={{ pageSizeLabel: "Show: " }} >
                                <div className="horizontalScroll inner-container vertical-scroll">
                                    <NeoGrid.Grid<TransactionsLookup>>
                                        {(item, meta) => (
                                            <NeoGrid.RowGroup expandProperty={meta.isExpanded}>
                                                <NeoGrid.Row>
                                                    <NeoGrid.Column display={meta.eventDate} />
                                                    <NeoGrid.Column display={meta.clientNumber} />
                                                    <NeoGrid.Column display={meta.stockbrokerAccount} />
                                                    <NeoGrid.Column display={meta.eventName} />
                                                    <NeoGrid.Column display={meta.eventID} label={TextConstants.Labels.TransactionID}/>
                                                    <NeoGrid.Column display={meta.relatedEventID} label={TextConstants.Labels.RelatedTrasactionID}/>
                                                    <NeoGrid.Column display={meta.instrumentDescription} />
                                                    <NeoGrid.Column display={meta.ticker} />
                                                    <NeoGrid.Column display={meta.isin} label={TextConstants.Labels.Isin}/>
                                                    <NeoGrid.Column display={meta.currency} label={<InfoLabelHover title={TextConstants.Labels.Currency} content={TextConstants.Tooltips.CurrencyInfo}/>}/>
                                                    <NeoGrid.Column display={meta.amount} label={TextConstants.Labels.TransactionAmount} cellClassName="text-right" />
                                                    <NeoGrid.Column display={meta.amountUSD} label={TextConstants.Labels.TransactionAmountNC} cellClassName="text-right" />
                                                    <NeoGrid.Column display={meta.quantity} cellClassName="text-right" />
                                                    <NeoGrid.Column display={meta.isExcludedInd} label={<InfoLabelHover title={TextConstants.Labels.Excluded} content={TextConstants.Tooltips.ExcludedIndInfo}/>}/>
                                                </NeoGrid.Row>
                                                {item.isExcludedInd === false &&
                                                    <NeoGrid.ChildRow>
                                                        <NeoGrid.Grid items={item.transactionDetail}>
                                                            {(child, childMeta) => (
                                                                <NeoGrid.Row>
                                                                    <NeoGrid.Column display={childMeta.portfolioQuantity} />
                                                                    <NeoGrid.Column display={childMeta.costOfSale} cellClassName="text-right" />
                                                                    <NeoGrid.Column display={childMeta.purchaseCost} cellClassName="text-right" />
                                                                    <NeoGrid.Column display={childMeta.portfolioBookCost} cellClassName="text-right" />
                                                                    <NeoGrid.Column display={childMeta.costOfSaleUSD} cellClassName="text-right" />
                                                                    <NeoGrid.Column display={childMeta.purchaseCostUSD} cellClassName="text-right" />
                                                                    <NeoGrid.Column display={childMeta.portfolioBookCostUSD} cellClassName="text-right" />
                                                                </NeoGrid.Row>
                                                            )}
                                                        </NeoGrid.Grid>
                                                    </NeoGrid.ChildRow>
                                                }
                                            </NeoGrid.RowGroup>
                                        )}
                                    </NeoGrid.Grid>
                                </div>
                            </Neo.Pager>
                        </>
                    }
                    {this.viewModel.transactionsPageManager.totalRecords === 0 &&
                        <div className="col-md-12 p-0 text-center">
                            {TextConstants.GeneralText.NoRecords}
                        </div>
                    }
                </CollapsibleCard>
            </div>
        )
    }
}